import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  AppBar,
  Toolbar,
  Button,
  IconButton,
  Drawer,
  List,
  ListItem,
  ListItemText,
  Card,
  CardContent,
  Container,
  TextField,
  Fade,
  LinearProgress,
  InputAdornment,
  Tooltip,
  CircularProgress,
  Snackbar,
  Alert,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import { Link, useNavigate } from 'react-router-dom';
import CryptoJS from 'crypto-js';
import axios from 'axios';
import { format } from 'date-fns';
import {
  FaChartLine,
  FaUsers,
  FaUser,
  FaSignOutAlt,
  FaBars,
  FaEye,
  FaEyeSlash,
} from 'react-icons/fa';
import LanguageSwitcher from '../components/LanguageSwitcher';
import i18n from '../i18n';
const ENCRYPTION_KEY = 'dadash123@@123##';

const Profile = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [user, setUser] = useState({});
  const [mobileOpen, setMobileOpen] = useState(false);
  const [newUsername, setNewUsername] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [errors, setErrors] = useState({ newUsername: '', newPassword: '', confirmPassword: '' });
  const [passwordStrength, setPasswordStrength] = useState(0);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');
  const [snackbarSeverity, setSnackbarSeverity] = useState('success');
  const [, forceUpdate] = useState();

  useEffect(() => {
    const encryptedData = localStorage.getItem('user');
    if (encryptedData) {
      try {
        const bytes = CryptoJS.AES.decrypt(encryptedData, ENCRYPTION_KEY);
        const decrypted = bytes.toString(CryptoJS.enc.Utf8);
        const userData = JSON.parse(decrypted);
        setUser(userData);
        setNewUsername(userData.UserName || '');
      } catch (err) {
        console.error('Failed to decrypt user data:', err);
        localStorage.removeItem('user');
        navigate('/');
      }
    } else {
      navigate('/');
    }
  }, [navigate]);

  useEffect(() => {
    const handleLanguageChange = () => {
      forceUpdate({});
    };
    i18n.on('languageChanged', handleLanguageChange);
    return () => {
      i18n.off('languageChanged', handleLanguageChange);
    };
  }, [i18n]);

  const handleLogout = () => {
    localStorage.removeItem('user');
    navigate('/');
  };

  const handleDrawerToggle = () => {
    setMobileOpen(!mobileOpen);
  };

  const calculatePasswordStrength = (password) => {
    let strength = 0;
    if (password.length > 0) strength += 20;
    if (password.length >= 8) strength += 20;
    if (/[A-Z]/.test(password)) strength += 20;
    if (/[0-9]/.test(password)) strength += 20;
    if (/[^A-Za-z0-9]/.test(password)) strength += 20;
    return strength;
  };

  const getPasswordStrengthLabel = () => {
    if (passwordStrength === 0) return t('profile.strength.none');
    if (passwordStrength < 40) return t('profile.strength.weak');
    if (passwordStrength < 60) return t('profile.strength.medium');
    if (passwordStrength < 80) return t('profile.strength.strong');
    return t('profile.strength.veryStrong');
  };

  const handlePasswordChange = (e) => {
    const pwd = e.target.value;
    setNewPassword(pwd);
    setPasswordStrength(calculatePasswordStrength(pwd));
    setErrors((prev) => ({
      ...prev,
      newPassword: calculatePasswordStrength(pwd) < 80 && pwd ? t('profile.passwordWeak') : '',
    }));
  };

  const handleConfirmPasswordChange = (e) => {
    const confirmPwd = e.target.value;
    setConfirmPassword(confirmPwd);
    setErrors((prev) => ({
      ...prev,
      confirmPassword: confirmPwd !== newPassword ? t('profile.passwordMismatch') : '',
    }));
  };

  const validateForm = () => {
    const newErrors = { newUsername: '', newPassword: '', confirmPassword: '' };
    let isValid = true;

    if (!newUsername.trim()) {
      newErrors.newUsername = t('profile.required');
      isValid = false;
    }
    if (newPassword && calculatePasswordStrength(newPassword) < 80) {
      newErrors.newPassword = t('profile.passwordWeak');
      isValid = false;
    }
    if (newPassword && newPassword !== confirmPassword) {
      newErrors.confirmPassword = t('profile.passwordMismatch');
      isValid = false;
    }

    setErrors(newErrors);
    if (!isValid) {
      const firstError =
        newErrors.newUsername ||
        newErrors.newPassword ||
        newErrors.confirmPassword;
      setSnackbarMessage(firstError);
      setSnackbarSeverity('error');
      setSnackbarOpen(true);
    }
    return isValid;
  };

  const handleCloseSnackbar = () => {
    setSnackbarOpen(false);
  };

  const handleSaveChanges = async () => {
    if (!validateForm()) return;

    setLoading(true);

    try {
      const response = await axios.post(
        'https://copyapi.avangar.ir/api/forex/setuserpass',
        {
          userId: user.UserID,
          username: newUsername,
          pass: newPassword || user.Pass,
        },
        {
          headers: {
            'Content-Type': 'application/json',
            'Accept': 'application/json',
          },
        }
      );

      const updatedUser = { ...user, UserName: newUsername };
      if (newPassword) {
        updatedUser.Pass = newPassword;
      }
      const encryptedData = CryptoJS.AES.encrypt(
        JSON.stringify(updatedUser),
        ENCRYPTION_KEY
      ).toString();
      localStorage.setItem('user', encryptedData);
      setUser(updatedUser);
      setSnackbarMessage(response.data.ErrorMSG || t('profile.success'));
      setSnackbarSeverity('success');
      setSnackbarOpen(true);
      // Reset form fields
      setNewUsername('');
      setNewPassword('');
      setConfirmPassword('');
      setPasswordStrength(0);
      setErrors({ newUsername: '', newPassword: '', confirmPassword: '' });
    } catch (err) {
      setSnackbarMessage(
        err.response?.data?.ErrorMSG ||
        err.response?.data?.Message ||
        t('profile.error')
      );
      setSnackbarSeverity('error');
      setSnackbarOpen(true);
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return '';
    try {
      const date = new Date(dateString);
      return format(date, 'yyyy-MM-dd HH:mm:ss');
    } catch {
      return '';
    }
  };

  const menuItems = [
    { text: t('home') || 'Home', icon: <FaChartLine />, path: '/home' },
    { text: t('subscribers') || 'Subscribers', icon: <FaUsers />, path: '/subscribers' },
    { text: t('signals.title') || 'Signals', icon: <FaChartLine />, path: '/signals' },
    { text: t('profile.title') || 'Profile', icon: <FaUser />, path: '/profile' },
  ];

  const drawer = (
    <Box sx={{ p: 2, bgcolor: '#fff' }}>
      <List>
        {menuItems.map((item) => (
          <ListItem
            key={item.text}
            component={Link}
            to={item.path}
            onClick={handleDrawerToggle}
            sx={{
              py: 1,
              '&:hover': {
                bgcolor: 'rgba(0,255,0,0.1)',
                boxShadow: '0 0 8px rgba(0,255,0,0.3)',
                transform: 'scale(1.02)',
              },
              transition: 'all 0.2s ease-in-out',
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Box sx={{ color: '#1e3c72' }}>{item.icon}</Box>
              <ListItemText primary={item.text} />
            </Box>
          </ListItem>
        ))}
        <ListItem
          onClick={handleLogout}
          sx={{
            py: 1,
            '&:hover': {
              bgcolor: 'rgba(255,0,0,0.1)',
              boxShadow: '0 0 8px rgba(255,0,0,0.3)',
              transform: 'scale(1.02)',
            },
            transition: 'all 0.2s ease-in-out',
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Box sx={{ color: '#1e3c72' }}>
              <FaSignOutAlt />
            </Box>
            <ListItemText primary={t('logout') || 'Logout'} />
          </Box>
        </ListItem>
      </List>
    </Box>
  );

  return (
    <Box
      sx={{
        minHeight: '100vh',
        background: 'linear-gradient(45deg, #1e3c72, #2a5298, rgba(0,255,0,0.3), rgba(255,0,0,0.3))',
        backgroundSize: '400% 400%',
        animation: 'backgroundGradient 15s ease infinite',
        position: 'relative',
      }}
    >
      <Box
        sx={{
          position: 'absolute',
          top: '10%',
          left: '5%',
          width: '200px',
          height: '200px',
          background: 'radial-gradient(circle, rgba(0, 255, 0, 0.3) 0%, transparent 70%)',
          animation: 'pulse 6s infinite ease-in-out',
        }}
      />
      <Box
        sx={{
          position: 'absolute',
          bottom: '10%',
          right: '5%',
          width: '200px',
          height: '200px',
          background: 'radial-gradient(circle, rgba(255, 0, 0, 0.3) 0%, transparent 70%)',
          animation: 'pulse 6s infinite ease-in-out reverse',
        }}
      />
      <style>
        {`
          @keyframes backgroundGradient {
            0% { background-position: 0% 50%; }
            50% { background-position: 100% 50%; }
            100% { background-position: 0% 50%; }
          }
          @keyframes pulse {
            0% { transform: scale(1); opacity: 0.5; }
            50% { transform: scale(1.2); opacity: 0.3; }
            100% { transform: scale(1); opacity: 0.5; }
          }
          @keyframes slideInLeft {
            0% { transform: translateX(-100px); opacity: 0; }
            100% { transform: translateX(0); opacity: 1; }
          }
          @keyframes slideInRight {
            0% { transform: translateX(100px); opacity: 0; }
            100% { transform: translateX(0); opacity: 1; }
          }
          @keyframes vibrate {
            0% { transform: translateX(0); }
            20% { transform: translateX(-2px); }
            40% { transform: translateX(2px); }
            60% { transform: translateX(-2px); }
            80% { transform: translateX(2px); }
            100% { transform: translateX(0); }
          }
          @keyframes winPulseGreen {
            0% { box-shadow: 0 0 0 0 rgba(0, 255, 0, 0.5); }
            50% { box-shadow: 0 0 15px 5px rgba(0, 255, 0, 0.3); }
            100% { box-shadow: 0 0 0 0 rgba(0, 255, 0, 0.5); }
          }
          @keyframes winPulseRed {
            0% { box-shadow: 0 0 0 0 rgba(255, 0, 0, 0.5); }
            50% { box-shadow: 0 0 15px 5px rgba(255, 0, 0, 0.3); }
            100% { box-shadow: 0 0 0 0 rgba(255, 0, 0, 0.5); }
          }
          @keyframes slideDown {
            0% { transform: translateY(-50px); opacity: 0; }
            100% { transform: translateY(0); opacity: 1; }
          }
          @keyframes gradientMove {
            0% { background-position: 0% 50%; }
            50% { background-position: 100% 50%; }
            100% { background-position: 0% 50%; }
          }
          @keyframes neonGlowGreen {
            0% { box-shadow: 0 0 5px rgba(0, 255, 0, 0.3); }
            50% { box-shadow: 0 0 20px rgba(0, 255, 0, 0.7); }
            100% { box-shadow: 0 0 5px rgba(0, 255, 0, 0.3); }
          }
          @keyframes neonGlowRed {
            0% { box-shadow: 0 0 5px rgba(255, 0, 0, 0.3); }
            50% { box-shadow: 0 0 20px rgba(255, 0, 0, 0.7); }
            100% { box-shadow: 0 0 5px rgba(255, 0, 0, 0.3); }
          }
          @keyframes softBounce {
            0% { transform: scale(1); }
            50% { transform: scale(0.95); }
            100% { transform: scale(1); }
          }
          @keyframes ripple {
            0% { box-shadow: 0 0 0 0 rgba(255, 255, 255, 0.5); }
            100% { box-shadow: 0 0 0 20px rgba(255, 255, 255, 0); }
          }
          @keyframes typing {
            0% { width: 0; }
            100% { width: 100%; }
          }
          @keyframes glow {
            0% { text-shadow: 0 0 5px rgba(0, 255, 0, 0.3), 0 0 5px rgba(255, 0, 0, 0.3); }
            50% { text-shadow: 0 0 15px rgba(0, 255, 0, 0.7), 0 0 15px rgba(255, 0, 0, 0.7); }
            100% { text-shadow: 0 0 5px rgba(0, 255, 0, 0.3), 0 0 5px rgba(255, 0, 0, 0.3); }
          }
          @keyframes fadeInDown {
            0% { transform: translateY(-20px); opacity: 0; }
            100% { transform: translateY(0); opacity: 1; }
          }
          .slide-in-left {
            animation: slideInLeft 0.5s ease-out forwards;
          }
          .slide-in-right {
            animation: slideInRight 0.5s ease-out forwards;
          }
        `}
      </style>
      <AppBar
        position="static"
        key={`appbar-${i18n.language}`}
        sx={{
          bgcolor: 'rgba(255,255,255,0.95)',
          boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
        }}
      >
        <Toolbar sx={{ justifyContent: 'space-between' }}>
          <Typography
            variant="h6"
            key={`title-${i18n.language}`}
            sx={{
              color: '#1e3c72',
              fontWeight: 'bold',
              textShadow: '0 0 8px rgba(0,255,0,0.2)',
            }}
          >
            {t('profile.title') || 'Profile'}
          </Typography>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <Box sx={{ display: { xs: 'none', md: 'flex' }, gap: 2 }}>
              {menuItems.map((item) => (
                <Button
                  key={item.text}
                  component={Link}
                  to={item.path}
                  startIcon={item.icon}
                  sx={{
                    color: '#1e3c72',
                    textTransform: 'none',
                    fontSize: '1rem',
                    '&:hover': {
                      color: '#fff',
                      bgcolor: '#2a5298',
                      boxShadow:
                        '0 0 10px rgba(0,255,0,0.4), 0 0 10px rgba(255,0,0,0.4)',
                      transform: 'scale(1.05)',
                    },
                    transition: 'all 0.2s ease-in-out',
                  }}
                >
                  {item.text}
                </Button>
              ))}
              <Button
                startIcon={<FaSignOutAlt />}
                onClick={handleLogout}
                sx={{
                  color: '#1e3c72',
                  textTransform: 'none',
                  fontSize: '1rem',
                  '&:hover': {
                    color: '#fff',
                    bgcolor: '#d32f2f',
                    boxShadow: '0 0 10px rgba(255,0,0,0.5)',
                    transform: 'scale(1.05)',
                  },
                  transition: 'all 0.2s ease-in-out',
                }}
              >
                {t('logout') || 'Logout'}
              </Button>
            </Box>
            <LanguageSwitcher />
            <IconButton
              color="inherit"
              edge="end"
              onClick={handleDrawerToggle}
              sx={{ display: { md: 'none' }, color: '#1e3c72' }}
            >
              <FaBars />
            </IconButton>
          </Box>
        </Toolbar>
      </AppBar>
      <Drawer
        anchor={i18n.language === 'fa' ? 'right' : 'left'}
        open={mobileOpen}
        onClose={handleDrawerToggle}
        sx={{ '& .MuiDrawer-paper': { width: 240 } }}
      >
        {drawer}
      </Drawer>
      <Container sx={{ py: 4 }}>
        <Fade in timeout={600}>
          <Card
            className={i18n.language === 'fa' ? 'slide-in-right' : 'slide-in-left'}
            sx={{
              maxWidth: 600,
              mx: 'auto',
              borderRadius: 3,
              boxShadow: `
                0 8px 24px rgba(0,0,0,0.2),
                0 0 20px rgba(0,255,0,0.2),
                0 0 20px rgba(255,0,0,0.2)
              `,
              bgcolor: 'rgba(255,255,255,0.95)',
              background: 'linear-gradient(45deg, rgba(255,255,255,0.9), rgba(255,255,255,0.9))',
              backgroundSize: '200% 200%',
              animation: 'gradientMove 10s ease infinite',
              '&:hover': {
                transform: 'translateY(-5px)',
                boxShadow: `
                  0 8px 20px rgba(0,0,0,0.2),
                  0 0 20px rgba(0,255,0,0.4),
                  0 0 20px rgba(255,0,0,0.4)
                `,
                // Removed vibrate animation to prevent jumpy effect
              },
              transition: 'transform 0.3s ease-in-out, box-shadow 0.3s ease-in-out',
            }}
          >
            <CardContent sx={{ p: { xs: 3, sm: 4 } }}>
              <Box sx={{ overflow: 'hidden', whiteSpace: 'nowrap' }}>
                <Typography
                  variant="h5"
                  sx={{
                    color: '#1e3c72',
                    fontWeight: 'bold',
                    mb: 3,
                    textShadow: '0 0 10px rgba(0,255,0,0.3)',
                    textAlign: 'center',
                    fontSize: { xs: '1.5rem', sm: '2rem' },
                    display: 'inline-block',
                    animation: 'typing 2s steps(30, end) forwards, glow 2s infinite',
                  }}
                >
                  {t('profile.title') || 'Profile'}
                </Typography>
              </Box>
              <Box sx={{ mb: 3 }}>
                <Typography sx={{ color: '#2a5298', mb: 1, fontWeight: 'bold' }}>
                  <Typography component="span" sx={{ fontWeight: 'bold' }}>{t('profile.name')}:</Typography> {user.Name || ''}
                </Typography>
                <Typography sx={{ color: '#2a5298', mb: 1, fontWeight: 'bold' }}>
                  <Typography component="span" sx={{ fontWeight: 'bold' }}>{t('profile.family')}:</Typography> {user.Family || ''}
                </Typography>
                <Typography sx={{ color: '#2a5298', mb: 1, fontWeight: 'bold' }}>
                  <Typography component="span" sx={{ fontWeight: 'bold' }}>{t('profile.nationalCode')}:</Typography> {user.NationalCode || ''}
                </Typography>
                <Typography sx={{ color: '#2a5298', mb: 1, fontWeight: 'bold' }}>
                  <Typography component="span" sx={{ fontWeight: 'bold' }}>{t('profile.mobileNo')}:</Typography> {user.MobileNO || ''}
                </Typography>
                <Typography sx={{ color: '#2a5298', mb: 1, fontWeight: 'bold' }}>
                  <Typography component="span" sx={{ fontWeight: 'bold' }}>{t('profile.startDate')}:</Typography> {formatDate(user.StartDate)}
                </Typography>
                <Typography sx={{ color: '#2a5298', mb: 1, fontWeight: 'bold' }}>
                  <Typography component="span" sx={{ fontWeight: 'bold' }}>{t('profile.endDate')}:</Typography> {formatDate(user.EndDate)}
                </Typography>
              </Box>

              <TextField
                label={t('profile.username')}
                value={newUsername}
                onChange={(e) => setNewUsername(e.target.value)}
                fullWidth
                margin="normal"
                error={!!errors.newUsername}
                variant="outlined"
                sx={{
                  '& .MuiOutlinedInput-root': {
                    borderRadius: 2,
                    '&:hover fieldset': { borderColor: '#2a5298' },
                    '&.Mui-focused fieldset': {
                      borderColor: '#1e3c72',
                      boxShadow: '0 0 8px rgba(0,255,0,0.3)',
                    },
                  },
                }}
              />
              <TextField
                label={t('profile.password')}
                type={showPassword ? 'text' : 'password'}
                value={newPassword}
                onChange={handlePasswordChange}
                fullWidth
                margin="normal"
                error={!!errors.newPassword}
                variant="outlined"
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton onClick={() => setShowPassword(!showPassword)}>
                        {showPassword ? <FaEyeSlash /> : <FaEye />}
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
                sx={{
                  '& .MuiOutlinedInput-root': {
                    borderRadius: 2,
                    '&:hover fieldset': { borderColor: '#2a5298' },
                    '&.Mui-focused fieldset': {
                      borderColor: '#1e3c72',
                      boxShadow: '0 0 8px rgba(255,0,0,0.3)',
                    },
                  },
                }}
              />
              {newPassword && (
                <Box sx={{ mt: 1, mb: 2 }}>
                  <Typography sx={{ color: '#2a5298', fontSize: '0.9rem' }}>
                    {t('profile.strength.title')}
                  </Typography>
                  <Tooltip title={getPasswordStrengthLabel()}>
                    <LinearProgress
                      variant="determinate"
                      value={passwordStrength}
                      sx={{
                        height: 8,
                        borderRadius: 4,
                        bgcolor: 'rgba(0,0,0,0.1)',
                        '& .MuiLinearProgress-bar': {
                          background: `linear-gradient(90deg, #d32f2f ${
                            passwordStrength < 60 ? 100 : 50
                          }%, #2a5298 ${passwordStrength >= 80 ? 100 : 50}%)`,
                        },
                      }}
                    />
                  </Tooltip>
                </Box>
              )}
              <TextField
                label={t('profile.confirmPassword')}
                type={showConfirmPassword ? 'text' : 'password'}
                value={confirmPassword}
                onChange={handleConfirmPasswordChange}
                fullWidth
                margin="normal"
                error={!!errors.confirmPassword}
                variant="outlined"
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                      >
                        {showConfirmPassword ? <FaEyeSlash /> : <FaEye />}
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
                sx={{
                  '& .MuiOutlinedInput-root': {
                    borderRadius: 2,
                    '&:hover fieldset': { borderColor: '#2a5298' },
                    '&.Mui-focused fieldset': {
                      borderColor: '#1e3c72',
                      boxShadow: '0 0 8px rgba(255,0,0,0.3)',
                    },
                  },
                }}
              />
              <Button
                variant="contained"
                onClick={handleSaveChanges}
                fullWidth
                disabled={loading}
                sx={{
                  mt: 3,
                  py: 1.5,
                  borderRadius: 2,
                  background: 'linear-gradient(90deg, #1e3c72 0%, #2a5298 100%)',
                  '&:hover': {
                    background: '#1e3c72',
                    transform: 'scale(1.05)',
                    boxShadow:
                      '0 0 12px rgba(0,255,0,0.4), 0 0 12px rgba(255,0,0,0.4)',
                    animation: 'neonGlowGreen 1s infinite',
                  },
                  '&:active': {
                    animation: 'softBounce 0.3s ease-in-out',
                  },
                  textTransform: 'none',
                  fontSize: '1.1rem',
                  boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
                  transition: 'all 0.2s ease-in-out',
                }}
              >
                {loading ? <CircularProgress size={24} color="inherit" /> : t('profile.save') || 'Save'}
              </Button>
            </CardContent>
          </Card>
        </Fade>
        <Snackbar
          open={snackbarOpen}
          autoHideDuration={4000}
          onClose={handleCloseSnackbar}
          anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
          sx={{
            '& .MuiSnackbarContent-root': {
              borderRadius: 2,
              boxShadow: '0 4px 12px rgba(0,0,0,0.2)',
              animation: 'fadeInDown 0.5s ease-out forwards',
            },
          }}
        >
          <Alert
            onClose={handleCloseSnackbar}
            severity={snackbarSeverity}
            sx={{
              width: '100%',
              bgcolor:
                snackbarSeverity === 'success' ? '#e8f5e9' : '#ffebee',
              color: snackbarSeverity === 'success' ? '#2e7d32' : '#c62828',
              '& .MuiAlert-icon': {
                color: snackbarSeverity === 'success' ? '#4caf50' : '#d32f2f',
              },
            }}
          >
            {snackbarMessage}
          </Alert>
        </Snackbar>
      </Container>
    </Box>
  );
};

export default Profile;